import { TextInitial } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type Section, type SectionVersion } from "~/api/types";
import { useSections } from "~/organisms/addin/taskpane/hooks/useSections";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "~/atoms/empty";
import HeadingPage from "~/molecules/HeadingPage";
import { SectionsPanel } from "~/organisms/SectionsPanel";
import { VersionsPanel } from "~/organisms/VersionsPanel";

export const SectionsOverview: React.FC = () => {
  const { t } = useTranslation();
  const { data: sections } = useSections();

  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<SectionVersion | null>(
    null,
  );

  const toggleSelectedSection = (section: Section) => {
    if (selectedSection?.id === section.id) {
      setSelectedSection(null);
      setSelectedVersion(null);
    } else {
      setSelectedSection(section);
      const sortedVersions = [...section.sectionVersions].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setSelectedVersion(sortedVersions[0] ?? null);
    }
  };

  const toggleSelectedVersion = (version: SectionVersion) => {
    if (selectedVersion?.id === version.id) {
      setSelectedVersion(null);
    } else {
      setSelectedVersion(version);
    }
  };

  return (
    <div className="container flex h-[calc(100vh-4rem)] flex-col gap-4 py-7">
      <HeadingPage title={t("commons.sections.title")} />
      {sections ? (
        <div className="flex min-h-0 flex-1 gap-12">
          <SectionsPanel
            sections={sections}
            onSectionClick={toggleSelectedSection}
            selectedSection={selectedSection}
          />
          {selectedSection ? (
            <VersionsPanel
              section={selectedSection}
              selectedVersion={selectedVersion}
              onVersionClick={toggleSelectedVersion}
            />
          ) : (
            <Empty className="h-fit">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <TextInitial className="mr-1 h-5 w-5" />
                </EmptyMedia>
                <EmptyTitle>
                  {t("commons.sections.noSectionSelected")}
                </EmptyTitle>
                <EmptyDescription>
                  {t("commons.sections.selectSectionDescription")}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>
      ) : (
        <Empty className="h-fit">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <TextInitial className="mr-1 h-5 w-5" />
            </EmptyMedia>
            <EmptyTitle>{t("commons.sections.noSectionsCreated")}</EmptyTitle>
            <EmptyDescription>
              {t("commons.sections.createSectionsHint")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </div>
  );
};
